import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Facebook, AlertCircle, Shirt, Wand2, MessageCircle, Bell } from 'lucide-react';
import { API_URL } from '../../config';
import StudioPro from './StudioPro';
import UniversalMarketing from './UniversalMarketing';
import CommentMonitoring from './CommentMonitoring';
import CreditsBanner from '../shared/CreditsBanner';

export default function PageSelector({ serviceType, onBack }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        const allPages = data.clients || [];

        // Filtrer selon le service ET charger les quotas pour chaque page
        const filteredPagesPromises = allPages.map(async (page) => {
          // Charger les quotas depuis /quotas
          let quotas = null;
          try {
            const quotasResponse = await fetch(
              `${API_URL}/api/clients/${page.$id}/quotas?service=studiopro`,
              {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
              }
            );
            if (quotasResponse.ok) {
              quotas = await quotasResponse.json();
            }
          } catch (err) {
            console.error('Error loading quotas for page:', page.$id, err);
          }

          // Déterminer si la page a des quotas pour ce service
          let hasQuotas = false;
          
          if (serviceType === 'vtryon') {
            // ✅ Modèle crédits : toutes les pages sont visibles — les crédits
            // achetés (pool utilisateur) sont transférés à la page choisie.
            hasQuotas = true;
          } else if (serviceType === 'universal') {
            // Universal : au moins un quota > 0
            hasQuotas = quotas && (
              (quotas.max_images_universal > 0) ||
              (quotas.max_videos_universal > 0)
            );
          } else if (serviceType === 'comment_monitoring') {
            // Comment Monitoring : vérifier si la page a une config active
            // Pour l'instant, on accepte toutes les pages (à affiner selon tes besoins)
            hasQuotas = true;
          }

          if (hasQuotas) {
            return { ...page, quotas }; // ✅ Ajoute les quotas à la page
          }
          return null;
        });

        const resolvedPages = await Promise.all(filteredPagesPromises);
        const filteredPages = resolvedPages.filter(p => p !== null);

        setPages(filteredPages);

        // ✅ VTry-on : accès direct à l'outil, sans étape de sélection —
        // la première page du compte est utilisée automatiquement
        if (serviceType === 'vtryon' && filteredPages.length >= 1) {
          selectPage(filteredPages[0]);
        } else if (filteredPages.length === 1) {
          // Autres services : auto-sélection si une seule page
          selectPage(filteredPages[0]);
        }
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sélection d'une page : pour VTry-on, transfère d'abord le pool de crédits
  // utilisateur vers cette page (les crédits achetés suivent l'utilisateur)
  const selectPage = async (page) => {
    if (serviceType === 'vtryon') {
      try {
        await fetch(`${API_URL}/api/bot/vtryon/claim/${page.$id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
      } catch (e) {
        console.error('Erreur transfert crédits:', e);
      }
    }
    setSelectedPage(page);
  };

  // Si page sélectionnée, afficher le composant correspondant
  if (selectedPage) {
    if (serviceType === 'vtryon') {
      return (
        <StudioPro
          clientId={selectedPage.$id}
          onBack={() => setSelectedPage(null)}
        />
      );
    } else if (serviceType === 'universal') {
      return (
        <UniversalMarketing
          clientId={selectedPage.$id}
          onBack={() => setSelectedPage(null)}
        />
      );
    } else if (serviceType === 'comment_monitoring') {
      return (
        <CommentMonitoring
          clientId={selectedPage.$id}
          onBack={() => setSelectedPage(null)}
        />
      );
    }
  }

  const serviceConfig = {
    vtryon: {
      title: 'TeckiA VTry-on',
      subtitle: 'Essayage virtuel mode IA',
      icon: Shirt,
      color: 'from-pink-600 to-rose-600'
    },
    universal: {
      title: 'Universal Marketing',
      subtitle: 'Marketing produits universel',
      icon: Wand2,
      color: 'from-cyan-600 to-blue-600'
    },
    comment_monitoring: {
      title: 'Surveillance Commentaires',
      subtitle: 'Détection et réponse automatique',
      icon: Bell,
      color: 'from-orange-600 to-red-600'
    }
  };

  const config = serviceConfig[serviceType] || serviceConfig.vtryon;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Chargement des pages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8 rounded-2xl">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/TeckiA-logo.gif" 
                alt="TeckiA IA" 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
              />
              <div>
                <h1 className="text-base md:text-xl font-bold text-white flex items-center gap-2">
                  <Icon className="w-5 h-5" />
                  {config.title}
                </h1>
                <p className="text-xs md:text-sm text-purple-200">{config.subtitle}</p>
              </div>
            </div>
            
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden md:inline">Retour</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto">
        {/* Crédits disponibles (modules à crédits) */}
        {serviceType === 'vtryon' && <CreditsBanner serviceId="teckia_vtryon" />}

        <div className={`bg-gradient-to-br ${config.color} rounded-3xl p-8 mb-8 text-center`}>
          <Icon className="w-16 h-16 text-white mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-2">
            Sélectionnez une page Facebook
          </h2>
          <p className="text-white/80 text-lg">
            {pages.length} page{pages.length > 1 ? 's' : ''} disponible{pages.length > 1 ? 's' : ''} pour ce service
          </p>
        </div>

        {pages.length === 0 ? (
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 text-center">
            <AlertCircle className="w-20 h-20 text-yellow-400 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-white mb-4">
              Aucune page active
            </h3>
            <p className="text-white/70 text-lg mb-8">
              Vous n'avez pas encore de page Facebook avec des quotas activés pour ce service.
            </p>
            <button
              onClick={onBack}
              className="px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
            >
              Retour au tableau de bord
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {pages.map(page => (
              <button
                key={page.$id}
                onClick={() => selectPage(page)}
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-purple-500/50 transition-all hover:scale-105 text-left"
              >
                <div className="flex items-start gap-4 mb-6">
                  {page.facebook_page_picture ? (
                    <img
                      src={page.facebook_page_picture}
                      alt={page.facebook_page_name}
                      className="w-16 h-16 rounded-xl object-cover border-2 border-white/20"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Facebook className="w-8 h-8 text-white" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-purple-300 transition-colors">
                      {page.facebook_page_name}
                    </h3>
                    <p className="text-white/60 text-sm">
                      Page Facebook active
                    </p>
                  </div>
                </div>

                {/* Quotas ou Infos selon le service */}
                <div className="space-y-3">
                  {serviceType === 'vtryon' && page.quotas && (
                    <>
                      {page.quotas.max_images > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/70 text-sm">📸 Images Studio</span>
                            <span className="text-green-400 font-bold text-lg">
                              {page.quotas.remaining_images} restantes
                            </span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all"
                              style={{ 
                                width: `${(page.quotas.remaining_images / page.quotas.max_images) * 100}%` 
                              }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-1">
                            {page.quotas.used_images} utilisées / {page.quotas.max_images} total
                          </p>
                        </div>
                      )}
                      {page.quotas.max_videos_wan > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/70 text-sm">🎬 Vidéos V12</span>
                            <span className="text-cyan-400 font-bold text-lg">
                              {page.quotas.remaining_videos_wan} restantes
                            </span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all"
                              style={{ 
                                width: `${(page.quotas.remaining_videos_wan / page.quotas.max_videos_wan) * 100}%` 
                              }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-1">
                            {page.quotas.used_videos_wan} utilisées / {page.quotas.max_videos_wan} total
                          </p>
                        </div>
                      )}
                      {page.quotas.max_videos_hailua > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/70 text-sm">⚡ Vidéos V15</span>
                            <span className="text-purple-400 font-bold text-lg">
                              {page.quotas.remaining_videos_hailua} restantes
                            </span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                              style={{ 
                                width: `${(page.quotas.remaining_videos_hailua / page.quotas.max_videos_hailua) * 100}%` 
                              }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-1">
                            {page.quotas.used_videos_hailua} utilisées / {page.quotas.max_videos_hailua} total
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {serviceType === 'universal' && page.quotas && (
                    <>
                      {page.quotas.max_images_universal > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/70 text-sm">📸 Images Universal</span>
                            <span className="text-green-400 font-bold text-lg">
                              {page.quotas.remaining_images_universal} restantes
                            </span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all"
                              style={{ 
                                width: `${(page.quotas.remaining_images_universal / page.quotas.max_images_universal) * 100}%` 
                              }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-1">
                            {page.quotas.used_images_universal} utilisées / {page.quotas.max_images_universal} total
                          </p>
                        </div>
                      )}
                      {page.quotas.max_videos_universal > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/70 text-sm">🎬 Vidéos Universal</span>
                            <span className="text-cyan-400 font-bold text-lg">
                              {page.quotas.remaining_videos_universal} restantes
                            </span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all"
                              style={{ 
                                width: `${(page.quotas.remaining_videos_universal / page.quotas.max_videos_universal) * 100}%` 
                              }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-1">
                            {page.quotas.used_videos_universal} utilisées / {page.quotas.max_videos_universal} total
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* ✅ COMMENT MONITORING - Infos surveillance */}
                  {serviceType === 'comment_monitoring' && (
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="w-5 h-5 text-orange-400" />
                        <span className="text-white font-medium text-sm">Surveillance active</span>
                      </div>
                      <div className="space-y-2 text-xs text-white/70">
                        <div className="flex items-center justify-between">
                          <span>✓ Détection commentaires</span>
                          <span className="text-green-400 font-bold">Activé</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>✓ Réponses automatiques</span>
                          <span className="text-green-400 font-bold">Activé</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>✓ IA TeckiA</span>
                          <span className="text-purple-400 font-bold">Prêt</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r ${config.color} text-white`}>
                    Ouvrir →
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}